import React, { useState, useEffect } from "react";

interface ArticleData {
  id: string;
  title: string;
  applicationStartDate: string | null;
  applicationEndDate: string | null;
  hasKfsFlag: boolean;
  isReserve: boolean;
}

interface EditKfsArticleFlagsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    applicationStartDate: string | null;
    applicationEndDate: string | null;
    hasKfsFlag: boolean;
    isReserve: boolean;
  }) => Promise<void>;
  article: ArticleData | null;
}

const EditKfsArticleFlagsModal: React.FC<EditKfsArticleFlagsModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  article,
}) => {
  const [applicationStartDate, setApplicationStartDate] = useState<string>("");
  const [applicationEndDate, setApplicationEndDate] = useState<string>("");
  const [hasKfsFlag, setHasKfsFlag] = useState(false);
  const [isReserve, setIsReserve] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (article && isOpen) {
      setApplicationStartDate(
        article.applicationStartDate
          ? article.applicationStartDate.split("T")[0]
          : ""
      );
      setApplicationEndDate(
        article.applicationEndDate
          ? article.applicationEndDate.split("T")[0]
          : ""
      );
      setHasKfsFlag(article.hasKfsFlag);
      setIsReserve(article.isReserve);
    }
  }, [article, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await onSubmit({
        applicationStartDate: applicationStartDate || null,
        applicationEndDate: applicationEndDate || null,
        hasKfsFlag,
        isReserve,
      });
      onClose();
    } catch (error) {
      console.error("Failed to update article flags:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen || !article) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="p-6">
          <h2 className="text-xl font-semibold mb-4">
            Edytuj flagi i daty naboru KFS
          </h2>

          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-2">Artykul:</p>
            <p className="text-sm font-medium text-gray-900 line-clamp-2">
              {article.title}
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label
                htmlFor="applicationStartDate"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Data rozpoczecia naboru
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="date"
                  id="applicationStartDate"
                  value={applicationStartDate}
                  onChange={(e) => setApplicationStartDate(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  disabled={isSubmitting}
                />
                <button
                  type="button"
                  onClick={() => setApplicationStartDate("")}
                  disabled={isSubmitting || !applicationStartDate}
                  className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 disabled:opacity-50"
                  title="Wyczysc date"
                >
                  Wyczysc
                </button>
              </div>
            </div>

            <div className="mb-4">
              <label
                htmlFor="applicationEndDate"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Data zakonczenia naboru
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="date"
                  id="applicationEndDate"
                  value={applicationEndDate}
                  onChange={(e) => setApplicationEndDate(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  disabled={isSubmitting}
                />
                <button
                  type="button"
                  onClick={() => setApplicationEndDate("")}
                  disabled={isSubmitting || !applicationEndDate}
                  className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 disabled:opacity-50"
                  title="Wyczysc date"
                >
                  Wyczysc
                </button>
              </div>
            </div>

            <div className="mb-4">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={hasKfsFlag}
                  onChange={(e) => setHasKfsFlag(e.target.checked)}
                  disabled={isSubmitting}
                  className="w-4 h-4 text-primary-700 border-gray-300 rounded focus:ring-primary-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  Flaga KFS (hasKfsFlag)
                </span>
              </label>
              <p className="text-xs text-gray-500 ml-7 mt-1">
                Oznacza artykul jako nabor KFS
              </p>
            </div>

            <div className="mb-6">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isReserve}
                  onChange={(e) => setIsReserve(e.target.checked)}
                  disabled={isSubmitting}
                  className="w-4 h-4 text-primary-700 border-gray-300 rounded focus:ring-primary-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  Rezerwa (isReserve)
                </span>
              </label>
              <p className="text-xs text-gray-500 ml-7 mt-1">
                Oznacza nabor jako rezerwe KFS
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Anuluj
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-700 border border-transparent rounded-md hover:bg-primary-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? "Zapisywanie..." : "Zapisz zmiany"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditKfsArticleFlagsModal;
